/**
 * "Select a clinic" — where a multi-clinic owner lands after signing in.
 *
 * ## Why an explicit screen
 *
 * Before this, login silently resolved to last-used-or-home. That is fine for
 * staff, who have exactly one clinic, but for an owner it means the app quietly
 * picks which clinic's patients you are about to see. Given that everything
 * downstream is scoped to that choice — patients, appointments, revenue — it
 * should be a decision, not a default.
 *
 * Shown only when there is a real choice to make: an org owner with more than
 * one clinic who has not asked to skip it. Everyone else goes straight through
 * (see RequireClinicSelection).
 *
 * Selecting here is NOT the same as switching mid-session — see enterClinic in
 * ClinicContext. There is normally nothing to drain and no reason to reload.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "aws-amplify/auth";

import KaagazyLogoWhite from "../../assets/logo-white.png";
import {
  ClinicSwitchBlockedError,
  useClinic,
} from "../../contexts/ClinicContext";
import { AccessibleClinic } from "../../services/organization.service";
import {
  getRememberedClinicId,
  setRememberClinic,
} from "../../utils/clinic-preference";

function initials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

function subtitle(clinic: AccessibleClinic): string {
  const parts: string[] = [];
  if (clinic.location) parts.push(clinic.location);
  parts.push(
    `${clinic.doctor_count} ${clinic.doctor_count === 1 ? "doctor" : "doctors"}`,
  );
  parts.push(
    `${clinic.staff_count} ${clinic.staff_count === 1 ? "staff" : "staff"}`,
  );
  return parts.join(" · ");
}

const SelectClinicPage: React.FC = () => {
  const navigate = useNavigate();
  const { clinics, organizationName, enterClinic, isLoading } = useClinic();

  const lastUsedId = getRememberedClinicId();
  const [remember, setRemember] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSelect = async (clinic: AccessibleClinic) => {
    setError(null);
    setBusyId(clinic.clinic_id);
    try {
      await enterClinic(clinic.clinic_id);
      // Only persist the skip preference once the choice actually succeeded —
      // remembering a clinic we failed to enter would skip this screen and
      // send them straight into the same failure next time.
      setRememberClinic(remember ? clinic.clinic_id : null);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(
        err instanceof ClinicSwitchBlockedError
          ? err.message
          : "Could not open that clinic. Please try again.",
      );
      setBusyId(null);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-100 p-4 sm:p-8">
      <div className="mx-auto flex w-full max-w-[1100px] overflow-hidden rounded-3xl bg-white shadow-xl">
        {/* Brand panel — hidden on small screens, where it costs the list room. */}
        <div
          className="hidden w-[42%] flex-col justify-between p-10 text-white lg:flex"
          style={{
            background: "linear-gradient(150deg, #1d4ed8 0%, #2f9ec4 60%, #3bb8a9 100%)",
          }}
        >
          <img
            src={KaagazyLogoWhite}
            alt="Kaagazy by SwasthiQ"
            className="h-12 w-auto self-start object-contain"
          />
          <div>
            <h1 className="text-[34px] font-bold leading-tight">
              <span className="text-white/70">Welcome back</span>
              <br />
              Choose a clinic
            </h1>
            <p className="mt-4 max-w-[320px] text-[13px] leading-relaxed text-white/80">
              Kaagazy keeps every clinic separate. Patients, appointments and
              revenue you see next belong only to the clinic you pick here.
            </p>
          </div>
          <div className="h-6" />
        </div>

        <div className="flex w-full flex-col px-6 py-10 sm:px-12 lg:w-[58%]">
          <h2 className="text-[26px] font-bold text-slate-900">
            Select a clinic
          </h2>
          <p className="mt-1 max-w-[440px] text-[13px] leading-relaxed text-slate-500">
            {organizationName
              ? `You have access to ${clinics.length} clinics under ${organizationName}. Pick the one you are working in right now.`
              : "Pick the clinic you are working in right now."}
          </p>

          {isLoading ? (
            <div className="mt-8 space-y-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-[76px] animate-pulse rounded-xl bg-slate-100"
                />
              ))}
            </div>
          ) : (
            <div className="mt-7 space-y-3">
              {clinics.map((clinic) => {
                const isLastUsed = clinic.clinic_id === lastUsedId;
                const isBusy = busyId === clinic.clinic_id;
                return (
                  <button
                    key={clinic.clinic_id}
                    onClick={() => handleSelect(clinic)}
                    disabled={busyId !== null}
                    className={`flex w-full items-center gap-4 rounded-xl border px-4 py-3.5 text-left transition-all disabled:opacity-60 ${
                      isLastUsed
                        ? "border-blue-500 ring-1 ring-blue-500/30"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-blue-600 text-[13px] font-bold text-white">
                      {initials(clinic.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold text-slate-900">
                        {clinic.name}
                      </span>
                      <span className="block truncate text-[12px] text-slate-500">
                        {subtitle(clinic)}
                      </span>
                    </span>
                    {!clinic.setup_complete && (
                      <span className="flex-shrink-0 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                        Setup incomplete
                      </span>
                    )}
                    {clinic.setup_complete && isLastUsed && (
                      <span className="flex-shrink-0 rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                        Last used
                      </span>
                    )}
                    <span className="flex-shrink-0 text-slate-300">
                      {isBusy ? (
                        <span className="block h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />
                      ) : (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {error && (
            <p className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-800">
              {error}
            </p>
          )}

          <label className="mt-6 flex cursor-pointer items-center gap-2.5 text-[13px] text-slate-600">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 accent-blue-600"
            />
            Remember this clinic and skip this screen next time
          </label>

          <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
            <button
              onClick={() => navigate("/onboarding/clinic-details?mode=branch")}
              className="flex items-center gap-1.5 text-[13px] font-semibold text-blue-600 hover:text-blue-700"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add another clinic
            </button>
            <button
              onClick={() => {
                // signOut fires the Amplify Hub event that flushes the local
                // cache, so no clinic data is left behind for the next person
                // on this device.
                void signOut().finally(() => navigate("/login", { replace: true }));
              }}
              className="text-[13px] text-slate-500 hover:text-slate-800"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectClinicPage;
